import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { profileService } from '../services/profileService';

export const MigrationDiscordBanner: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Fetch Discord profile to check if linked
  const { data: discordProfile, isLoading: discordLoading } = useQuery({
    queryKey: ['discordProfile'],
    queryFn: () => profileService.getDiscordProfile(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch frontend_state to check if dismissed
  const { data: stateData, isLoading: stateLoading } = useQuery({
    queryKey: ['frontendState', 'discordBanner'],
    queryFn: () => profileService.getFrontendState(),
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
  });

  const dismissed = stateData?.frontend_state?.discordBannerDismissed === true;

  if (!user || discordLoading || stateLoading || dismissed) {
    return null;
  }

  // Don't show if user already has Discord linked
  if (discordProfile?.discord_id) {
    return null;
  }

  const handleDismiss = async () => {
    try {
      await profileService.saveFrontendState({ discordBannerDismissed: true });
      queryClient.invalidateQueries({ queryKey: ['frontendState', 'discordBanner'] });
    } catch (error) {
      console.error('Failed to save banner dismissal:', error);
    }
  };

  return (
    <div
      className="bg-[#5865F2] bg-opacity-10 border-b border-[#5865F2] px-4 py-2 flex items-center justify-between"
      data-tour="discord-banner"
    >
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-[#5865F2] flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
          <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
        </svg>
        <span className="text-sm text-gray-300">
          Link your Discord account to join the trading community and get your roles.
        </span>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Link
          to="/profile?tab=discord"
          className="px-3 py-1 text-sm font-medium bg-[#5865F2] hover:bg-[#4752C4] text-white rounded transition-colors"
        >
          Link Discord
        </Link>
        <button
          onClick={handleDismiss}
          className="text-gray-500 hover:text-gray-400 transition-colors"
          aria-label="Dismiss"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
